/**
 * Management Page - Client Admins Only
 * Organization management including users, invitations, API keys, activity, and security policies
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, Key, Shield, Settings, Plus, Trash2, Copy, CheckCircle,
  AlertCircle, Loader2, Eye, EyeOff, UserPlus, Mail,
  MoreVertical, Activity, Clock, RefreshCw, UserX, UserCheck,
  X, AlertTriangle, BarChart3, Lock, DollarSign, FileText
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import ConfirmDialog, { useConfirm } from '../components/ConfirmDialog';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import ClientDocuments from '../components/ClientDocuments';
import { getErrorMessage } from '../services/apiService';
import { listApiKeys, createApiKey, revokeApiKey, ApiKey } from '../services/apiKeyService';
import { getClient, updateClientPolicies, listClientUsers, getMyPricing, Client, ClientUser, MyPricing, MyPricingMvrState } from '../services/clientService';
import {
  listInvitations, createInvitation, resendInvitation, cancelInvitation,
  Invitation, CreateInvitationRequest
} from '../services/invitationService';
import {
  updateUserStatus, updateUserRole, markUserForDeletion, restoreUser,
  updateUserPermissions, UserPermissions
} from '../services/userManagementService';
import { getActivitySummary, UserActivitySummary } from '../services/userActivityService';
import InvoiceRecipientsList from '../components/admin/InvoiceRecipientsList';

type TabType = 'users' | 'invitations' | 'apikeys' | 'documents' | 'settings' | 'pricing';
const validTabs: TabType[] = ['users', 'invitations', 'apikeys', 'documents', 'settings', 'pricing'];

const Management: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL or default to 'users'
  const getInitialTab = (): TabType => {
    const tabParam = searchParams.get('tab');
    if (tabParam && validTabs.includes(tabParam as TabType)) {
      return tabParam as TabType;
    }
    return 'users';
  };

  const [activeTab, setActiveTabState] = useState<TabType>(getInitialTab);

  // Update URL when tab changes
  const setActiveTab = useCallback((tab: TabType) => {
    setActiveTabState(tab);
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);

  // Redirect if not authenticated or not ClientAdmin
  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize

    if (!user) {
      navigate('/login');
    } else if (user.role !== 'ClientAdmin') {
      navigate('/profile');
    }
  }, [user, authLoading, navigate]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  // Client data
  const [client, setClient] = useState<Client | null>(null);
  const [loadingClient, setLoadingClient] = useState(true);

  // Users
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState<CreateInvitationRequest>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'ClientUser'
  });
  const [inviting, setInviting] = useState(false);
  const [userActionMenu, setUserActionMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // User Activity Modal
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [selectedUserActivity, setSelectedUserActivity] = useState<UserActivitySummary | null>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // User Permissions Modal
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState<ClientUser | null>(null);
  const [permissionsForm, setPermissionsForm] = useState<UserPermissions>({
    canAccessNmvtis: true,
    canAccessTheftLien: true,
    canAccessMotorVehicleRecord: true,
    canAccessVinDecoder: true,
    canAccessAstsPublic: true,
    canAccessInvoicing: false,
    canManageClients: false,
  });
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Invitations
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  // API Keys
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  // Settings
  const [policies, setPolicies] = useState({
    passwordExpirationDays: 0,
    enableApiKeyAccess: false
  });
  const [savingPolicies, setSavingPolicies] = useState(false);

  // Pricing
  const [pricing, setPricing] = useState<MyPricing | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(false);

  // Confirmation Dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant: 'danger' | 'warning';
  } | null>(null);

  const loadClientData = useCallback(async () => {
    if (!user?.clientId) {
      setLoadingClient(false);
      return;
    }

    try {
      setLoadingClient(true);
      const clientData = await getClient(user.clientId);
      setClient(clientData);
      setPolicies({
        passwordExpirationDays: clientData.passwordExpirationDays || 0,
        enableApiKeyAccess: clientData.enableApiKeyAccess
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingClient(false);
    }
  }, [user?.clientId]);

  const loadUsers = useCallback(async () => {
    if (!user?.clientId) return;

    try {
      setLoadingUsers(true);
      const userData = await listClientUsers(user.clientId);
      setUsers(userData);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingUsers(false);
    }
  }, [user?.clientId]);

  const loadInvitations = useCallback(async () => {
    try {
      setLoadingInvitations(true);
      const data = await listInvitations();
      setInvitations(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingInvitations(false);
    }
  }, []);

  const loadApiKeys = useCallback(async () => {
    try {
      setLoadingKeys(true);
      const keys = await listApiKeys();
      setApiKeys(keys);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    if (user?.clientId) {
      loadClientData();
    }
  }, [user?.clientId, loadClientData]);

  const loadPricing = useCallback(async () => {
    try {
      setLoadingPricing(true);
      const data = await getMyPricing();
      setPricing(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingPricing(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.clientId) return;

    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'invitations') {
      loadInvitations();
    } else if (activeTab === 'apikeys') {
      loadApiKeys();
    } else if (activeTab === 'pricing') {
      loadPricing();
    }
  }, [activeTab, user?.clientId, loadUsers, loadInvitations, loadApiKeys, loadPricing]);

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();

    setInviting(true);
    setError('');

    try {
      await createInvitation(inviteData);
      setSuccess('Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteData({ email: '', firstName: '', lastName: '', role: 'ClientUser' });
      loadInvitations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendInvitation(invitationId);
      setSuccess('Invitation resent successfully!');
      loadInvitations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId);
      setSuccess('Invitation cancelled.');
      loadInvitations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleToggleUserStatus = async (targetUser: ClientUser) => {
    setUserActionMenu(null);

    try {
      await updateUserStatus(targetUser.id, !targetUser.isActive);
      setSuccess(`User ${targetUser.isActive ? 'deactivated' : 'activated'} successfully!`);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleChangeRole = async (targetUser: ClientUser) => {
    setUserActionMenu(null);
    const newRole = targetUser.role === 'ClientAdmin' ? 'ClientUser' : 'ClientAdmin';

    try {
      await updateUserRole(targetUser.id, newRole as 'ClientUser' | 'ClientAdmin');
      setSuccess(`User role changed to ${newRole}!`);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDeleteUser = async (targetUser: ClientUser) => {
    setUserActionMenu(null);
    setConfirmDialog({
      show: true,
      title: 'Mark User for Deletion',
      message: `Are you sure you want to mark ${targetUser.firstName} ${targetUser.lastName} for deletion? They will have 30 days to be restored before permanent deletion.`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await markUserForDeletion(targetUser.id);
          setSuccess('User marked for deletion. They can be restored within 30 days.');
          loadUsers();
          setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
          setError(getErrorMessage(err));
        }
      }
    });
  };

  const handleRestoreUser = async (targetUser: ClientUser) => {
    setUserActionMenu(null);

    try {
      await restoreUser(targetUser.id);
      setSuccess('User restored successfully!');
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleViewActivity = async (targetUser: ClientUser) => {
    setUserActionMenu(null);
    setLoadingActivity(true);
    setShowActivityModal(true);

    try {
      const activity = await getActivitySummary(targetUser.id);
      setSelectedUserActivity(activity);
    } catch (err) {
      setError(getErrorMessage(err));
      setShowActivityModal(false);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleOpenPermissions = (targetUser: ClientUser) => {
    setUserActionMenu(null);
    setPermissionsUser(targetUser);
    setPermissionsForm({
      canAccessNmvtis: targetUser.canAccessNmvtis !== false,
      canAccessTheftLien: targetUser.canAccessTheftLien !== false,
      canAccessMotorVehicleRecord: targetUser.canAccessMotorVehicleRecord !== false,
      canAccessVinDecoder: targetUser.canAccessVinDecoder !== false,
      canAccessAstsPublic: targetUser.canAccessAstsPublic !== false,
      canAccessInvoicing: targetUser.canAccessInvoicing === true,
      canManageClients: targetUser.canManageClients === true,
      mfaEnabled: targetUser.mfaEnabled !== false,
      receivePortalAlerts: targetUser.receivePortalAlerts === true,
    });
    setShowPermissionsModal(true);
  };

  const handleSavePermissions = async () => {
    if (!permissionsUser) return;
    setSavingPermissions(true);
    setError('');

    try {
      await updateUserPermissions(permissionsUser.id, permissionsForm);
      setSuccess('Section permissions updated successfully.');
      setShowPermissionsModal(false);
      setPermissionsUser(null);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingKey(true);
    setError('');

    try {
      const response = await createApiKey({ name: keyName });
      setNewSecret(response.apiKey);
      setShowSecret(false);
      setKeyName('');
      loadApiKeys();
      setSuccess('API key created successfully! Make sure to copy it now.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(getErrorMessage(err));
      setShowCreateKeyModal(false);
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = (keyId: string) => {
    confirm({
      title: 'Revoke this API key?',
      message: 'Any integration still using it stops working immediately. This cannot be undone — a replacement key has to be issued.',
      confirmLabel: 'Revoke key',
      tone: 'danger',
      action: () => doRevokeKey(keyId),
    });
  };

  const doRevokeKey = async (keyId: string) => {
    try {
      await revokeApiKey(keyId);
      setSuccess('API key revoked successfully!');
      loadApiKeys();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleSavePolicies = async () => {
    if (!user?.clientId) return;

    setSavingPolicies(true);
    setError('');

    try {
      const updated = await updateClientPolicies(user.clientId, {
        passwordExpirationDays: policies.passwordExpirationDays > 0 ? policies.passwordExpirationDays : undefined,
        enableApiKeys: policies.enableApiKeyAccess
      });
      setClient(updated);
      setSuccess('Security policies updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSavingPolicies(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInvitationStatusColor = (status: string) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-500/15 text-yellow-700';
      case 'Accepted': return 'bg-green-500/15 text-green-700';
      case 'Expired': return 'bg-gray-500/15 text-gray-700';
      case 'Cancelled': return 'bg-red-500/15 text-red-700';
      default: return 'bg-gray-500/15 text-gray-700';
    }
  };

  if (authLoading || loadingClient) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Main Content - offset for sidebar on large screens */}
      <div className="pt-28 md:pt-32 lg:ml-[220px] flex-1">
        {/* Header */}
        <div className="relative z-20 px-4 md:px-8 py-6">
          <div className="max-w-6xl mx-auto">
            <div>
              <h1 className="text-3xl font-bold text-[#0f172a]">Organization Management</h1>
              <p className="text-[#64748b] mt-1">{client?.name || 'Your Organization'}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 px-4 md:px-8 py-8">
          <div className="max-w-6xl mx-auto">
          {/* Global Messages */}
          {error && (
            <div className="mb-6 p-4 rounded-xl border flex items-start space-x-3"
              style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
              <button onClick={() => setError('')} className="ml-auto">
                <X className="w-4 h-4 text-red-600" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-xl border flex items-center space-x-3"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)' }}>
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {/* Tabs */}
          <div data-tour="org-tabs" className="mb-6 flex space-x-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === 'users'
                  ? 'bg-gradient-to-r from-[#AD0000] to-red-700 text-white shadow-xl shadow-red-500/30'
                  : 'glass-card-strong text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <Users className="w-5 h-5 inline mr-2" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === 'invitations'
                  ? 'bg-gradient-to-r from-[#AD0000] to-red-700 text-white shadow-xl shadow-red-500/30'
                  : 'glass-card-strong text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <Mail className="w-5 h-5 inline mr-2" />
              Invitations
            </button>
            <button
              onClick={() => setActiveTab('apikeys')}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === 'apikeys'
                  ? 'bg-gradient-to-r from-[#AD0000] to-red-700 text-white shadow-xl shadow-red-500/30'
                  : 'glass-card-strong text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <Key className="w-5 h-5 inline mr-2" />
              API Keys
            </button>
            {/* This page already turns away anyone who is not a ClientAdmin, so the tab needs
                no further guard. The API checks the caller's claims regardless. */}
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === 'documents'
                  ? 'bg-gradient-to-r from-[#AD0000] to-red-700 text-white shadow-xl shadow-red-500/30'
                  : 'glass-card-strong text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <FileText className="w-5 h-5 inline mr-2" />
              Documents
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === 'settings'
                  ? 'bg-gradient-to-r from-[#AD0000] to-red-700 text-white shadow-xl shadow-red-500/30'
                  : 'glass-card-strong text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <Settings className="w-5 h-5 inline mr-2" />
              Settings
            </button>
            <button
              onClick={() => setActiveTab('pricing')}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === 'pricing'
                  ? 'bg-gradient-to-r from-[#AD0000] to-red-700 text-white shadow-xl shadow-red-500/30'
                  : 'glass-card-strong text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              <DollarSign className="w-5 h-5 inline mr-2" />
              Pricing
            </button>
          </div>

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#0f172a]">Organization Users</h2>
                <button
                  onClick={() => { setError(''); setShowInviteModal(true); }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium transition-all flex items-center space-x-2 shadow-lg shadow-green-500/20"
                >
                  <UserPlus className="w-5 h-5" />
                  <span>Invite User</span>
                </button>
              </div>

              {loadingUsers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#c41e3a]" />
                </div>
              ) : (
                <div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-black/[0.08]">
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#64748b]">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#64748b]">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#64748b]">Role</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#64748b]">Permissions</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#64748b]">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-[#64748b]">Last Login</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-[#64748b]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b border-black/[0.08] hover:bg-black/[0.02] transition-colors">
                          <td className="py-4 px-4">
                            <div className="font-medium text-[#0f172a]">{u.firstName} {u.lastName}</div>
                          </td>
                          <td className="py-4 px-4 text-[#64748b]">{u.email}</td>
                          <td className="py-4 px-4">
                            <span className="px-2 py-1 rounded-lg text-xs font-medium" style={{
                              backgroundColor: u.role === 'ClientAdmin' ? 'rgba(147, 51, 234, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                              color: u.role === 'ClientAdmin' ? '#7c3aed' : '#3b82f6'
                            }}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-1.5">
                              {[
                                { enabled: u.canAccessNmvtis, label: 'NMVTIS' },
                                { enabled: u.canAccessTheftLien, label: 'Theft/Lien' },
                                { enabled: u.canAccessMotorVehicleRecord, label: 'MVR' },
                                { enabled: u.canAccessVinDecoder, label: 'VIN Decoder' },
                                { enabled: u.canAccessAstsPublic, label: 'ASTS Public' },
                              ].map((p, i) => (
                                <span
                                  key={i}
                                  className={`w-2.5 h-2.5 rounded-full ${p.enabled !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                                  title={`${p.label}: ${p.enabled !== false ? 'Enabled' : 'Disabled'}`}
                                />
                              ))}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            {u.markedForDeletionAt ? (
                              <span className="px-2 py-1 rounded-lg text-xs font-medium bg-orange-500/15 text-orange-700">
                                Pending Deletion
                              </span>
                            ) : (
                              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                                u.isActive ? 'bg-green-500/15 text-green-700' : 'bg-red-500/15 text-red-700'
                              }`}>
                                {u.isActive ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-[#64748b] text-sm">{formatDate(u.lastLoginAt)}</td>
                          <td className="py-4 px-4 text-right">
                            {u.id !== user?.id && (
                              <button
                                ref={(el) => { menuButtonRefs.current[u.id] = el; }}
                                onClick={() => {
                                  if (userActionMenu === u.id) {
                                    setUserActionMenu(null);
                                  } else {
                                    const rect = menuButtonRefs.current[u.id]?.getBoundingClientRect();
                                    if (rect) {
                                      setMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
                                    }
                                    setUserActionMenu(u.id);
                                  }
                                }}
                                className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors text-[#64748b]"
                              >
                                <MoreVertical className="w-5 h-5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <div className="text-center py-12 text-[#94a3b8]">
                      No users in your organization yet. Start by inviting someone!
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Invitations Tab */}
          {activeTab === 'invitations' && (
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#0f172a]">Pending Invitations</h2>
                <button
                  onClick={() => { setError(''); setShowInviteModal(true); }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium transition-all flex items-center space-x-2 shadow-lg shadow-green-500/20"
                >
                  <UserPlus className="w-5 h-5" />
                  <span>Send Invitation</span>
                </button>
              </div>

              {loadingInvitations ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#c41e3a]" />
                </div>
              ) : (
                <div className="space-y-4">
                  {invitations.map((inv) => (
                    <div key={inv.id} className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08] flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-medium text-[#0f172a]">{inv.firstName} {inv.lastName}</h3>
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getInvitationStatusColor(inv.status)}`}>
                            {inv.status}
                          </span>
                        </div>
                        <p className="text-sm text-[#64748b]">{inv.email}</p>
                        <div className="mt-2 flex items-center space-x-4 text-xs text-[#94a3b8]">
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            Sent: {formatDateTime(inv.createdAt)}
                          </span>
                          {inv.status === 'Pending' && (
                            <span className="flex items-center text-yellow-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Expires: {formatDateTime(inv.expiresAt)}
                            </span>
                          )}
                          {inv.acceptedAt && (
                            <span className="flex items-center text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Accepted: {formatDateTime(inv.acceptedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      {inv.status === 'Pending' && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleResendInvitation(inv.id)}
                            className="px-3 py-2 rounded-lg bg-blue-500/15 text-blue-700 hover:bg-blue-500/25 transition-colors flex items-center space-x-1"
                          >
                            <RefreshCw className="w-4 h-4" />
                            <span className="text-sm">Resend</span>
                          </button>
                          <button
                            onClick={() => handleCancelInvitation(inv.id)}
                            className="px-3 py-2 rounded-lg bg-red-500/15 text-red-700 hover:bg-red-500/25 transition-colors flex items-center space-x-1"
                          >
                            <X className="w-4 h-4" />
                            <span className="text-sm">Cancel</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {invitations.length === 0 && (
                    <div className="text-center py-12 text-[#94a3b8]">
                      No invitations sent yet. Start building your team!
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* API Keys Tab */}
          {activeTab === 'apikeys' && (
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#0f172a]">API Keys</h2>
                <button
                  onClick={() => setShowCreateKeyModal(true)}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-medium transition-all flex items-center space-x-2 shadow-lg shadow-green-500/20"
                >
                  <Plus className="w-5 h-5" />
                  <span>Create API Key</span>
                </button>
              </div>

              {loadingKeys ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#c41e3a]" />
                </div>
              ) : (
                <div className="space-y-4">
                  {apiKeys.map((key) => (
                    <div key={key.id} className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08]">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-lg text-[#0f172a]">{key.name}</h3>
                            <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                              key.isActive ? 'bg-green-500/15 text-green-700' : 'bg-red-500/15 text-red-700'
                            }`}>
                              {key.isActive ? 'Active' : 'Revoked'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-[#64748b]">
                            <span className="font-mono">{key.keyPreview}</span>
                            <button
                              onClick={() => copyToClipboard(key.keyPreview)}
                              className="p-1 hover:bg-black/[0.05] rounded transition-colors"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="mt-2 text-xs text-[#94a3b8]">
                            Created: {formatDate(key.createdAt)} | Last used: {formatDate(key.lastUsedAt)}
                          </div>
                        </div>
                        {key.isActive && (
                          <button
                            onClick={() => handleRevokeKey(key.id)}
                            className="px-3 py-2 rounded-lg bg-red-500/15 text-red-700 hover:bg-red-500/25 transition-colors flex items-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-sm">Revoke</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {apiKeys.length === 0 && (
                    <div className="text-center py-12 text-[#94a3b8]">
                      No API keys created yet. Create one to get started.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              {loadingPricing ? (
                <div className="glass-card p-12 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
                </div>
              ) : pricing ? (
                <>
                  {/* Service Pricing */}
                  <div className="glass-card p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-rose-500/15">
                        <DollarSign className="w-6 h-6 text-[#c41e3a]" />
                      </div>
                      <h2 className="text-xl font-semibold text-[#0f172a]">Service Pricing</h2>
                    </div>
                    <div className="divide-y divide-black/[0.06]">
                      {pricing.services.map((svc) => (
                        <div key={svc.serviceType} className="flex items-center justify-between py-3">
                          <span className="font-medium text-[#0f172a]">{svc.serviceType}</span>
                          <span className="font-semibold text-[#0f172a]">${svc.effectivePrice.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* MVR Pricing by State */}
                  <div className="glass-card p-6">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-rose-500/15">
                        <DollarSign className="w-6 h-6 text-[#c41e3a]" />
                      </div>
                      <h2 className="text-xl font-semibold text-[#0f172a]">MVR Pricing by State</h2>
                    </div>
                    <div className="divide-y divide-black/[0.06]">
                      {pricing.mvrByState.map((s: MyPricingMvrState) => (
                        <div key={s.stateCode} className="flex items-center justify-between py-3">
                          <span className="font-medium text-[#0f172a]">{s.stateCode} — {s.stateName}</span>
                          <span className="font-semibold text-[#0f172a]">${s.effectivePrice.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="glass-card p-12 text-center text-[#64748b]">
                  No pricing information available.
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'documents' && (
            <div className="glass-card p-6">
              <ClientDocuments clientId={user?.clientId || ''} />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="glass-card p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-rose-500/15">
                  <Shield className="w-6 h-6 text-[#c41e3a]" />
                </div>
                <h2 className="text-xl font-semibold text-[#0f172a]">Security Policies</h2>
              </div>

              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08]">
                  <h3 className="font-medium mb-3 text-[#0f172a]">Password Expiration</h3>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      min="0"
                      value={policies.passwordExpirationDays}
                      onChange={(e) => setPolicies({ ...policies, passwordExpirationDays: parseInt(e.target.value) || 0 })}
                      className="w-24 px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a]"
                    />
                    <span className="text-[#64748b]">days (0 = no expiration)</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08]">
                  <div>
                    <h3 className="font-medium mb-1 text-[#0f172a]">Enable API Key Access</h3>
                    <p className="text-sm text-[#64748b]">Allow API key creation for programmatic access</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={policies.enableApiKeyAccess}
                      onChange={(e) => setPolicies({ ...policies, enableApiKeyAccess: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>

                <button
                  onClick={handleSavePolicies}
                  disabled={savingPolicies}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingPolicies ? (
                    <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Saving...</>
                  ) : (
                    'Save Policies'
                  )}
                </button>
              </div>

              {/* Invoice Recipients */}
              {user?.clientId && (
                <div className="mt-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-rose-500/15">
                      <Mail className="w-5 h-5 text-[#c41e3a]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#0f172a]">Invoice Recipients</h2>
                      <p className="text-xs text-[#64748b]">
                        Email addresses that receive invoices for your organization.
                      </p>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08]">
                    <InvoiceRecipientsList clientId={user.clientId} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      <Footer />

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full p-6 border border-black/[0.08] shadow-2xl">
            <h3 className="text-xl font-bold mb-4 text-[#0f172a]">Invite New User</h3>
            {error && (
              <div className="mb-4 p-3 rounded-xl border flex items-start space-x-3"
                style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <form onSubmit={handleInviteUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-[#0f172a]">Email</label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[#0f172a]">First Name</label>
                <input
                  type="text"
                  value={inviteData.firstName}
                  onChange={(e) => setInviteData({ ...inviteData, firstName: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[#0f172a]">Last Name</label>
                <input
                  type="text"
                  value={inviteData.lastName}
                  onChange={(e) => setInviteData({ ...inviteData, lastName: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-[#0f172a]">Role</label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as 'ClientUser' | 'ClientAdmin' })}
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="ClientUser">User</option>
                  <option value="ClientAdmin">Admin</option>
                </select>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={inviting}
                  className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-medium disabled:opacity-50 hover:from-green-700 hover:to-green-800 transition-all"
                >
                  {inviting ? <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Sending...</> : 'Send Invite'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create API Key Modal */}
      {showCreateKeyModal && !newSecret && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full p-6 border border-black/[0.08] shadow-2xl">
            <h3 className="text-xl font-bold mb-4 text-[#0f172a]">Create API Key</h3>
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-[#0f172a]">Key Name</label>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  required
                  placeholder="e.g., Production Server"
                  className="w-full px-4 py-2 rounded-xl border border-black/[0.08] bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={creatingKey}
                  className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 text-white font-medium disabled:opacity-50 hover:from-green-700 hover:to-green-800 transition-all"
                >
                  {creatingKey ? <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Creating...</> : 'Create Key'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateKeyModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Show Secret Modal */}
      {newSecret && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-2xl w-full p-6 border border-black/[0.08] shadow-2xl">
            <div className="flex items-center space-x-3 mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <h3 className="text-xl font-bold text-[#0f172a]">API Key Created!</h3>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
              <p className="text-yellow-800 text-sm">
                <strong>Important:</strong> Copy this API key now. It will not be shown again!
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-[#64748b]">API Key</label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 px-4 py-2 rounded-lg bg-white/60 backdrop-blur-sm border border-black/[0.08] text-[#0f172a] font-mono text-sm break-all">
                  {showSecret ? newSecret : '\u2022'.repeat(60)}
                </code>
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors text-[#64748b]"
                >
                  {showSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => copyToClipboard(newSecret)}
                  className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors text-[#64748b]"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setNewSecret(null);
                setShowCreateKeyModal(false);
                setShowSecret(false);
              }}
              className="btn-primary w-full mt-6"
            >
              I've Saved the API Key
            </button>
          </div>
        </div>
      )}

      {/* User Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 border border-black/[0.08] shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#0f172a]">User Activity</h3>
              <button
                onClick={() => {
                  setShowActivityModal(false);
                  setSelectedUserActivity(null);
                }}
                className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors text-[#64748b]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingActivity ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
              </div>
            ) : selectedUserActivity ? (
              <div className="space-y-6">
                {/* User Info */}
                <div className="flex items-center space-x-4 p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08]">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xl font-bold text-white">
                    {selectedUserActivity.fullName.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-[#0f172a]">{selectedUserActivity.fullName}</h4>
                    <p className="text-[#64748b]">{selectedUserActivity.email}</p>
                  </div>
                  <div className="ml-auto flex items-center space-x-2">
                    {selectedUserActivity.isActive ? (
                      <span className="px-2 py-1 rounded-lg text-xs font-medium bg-green-500/15 text-green-700">Active</span>
                    ) : (
                      <span className="px-2 py-1 rounded-lg text-xs font-medium bg-red-500/15 text-red-700">Inactive</span>
                    )}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08] text-center">
                    <div className="text-2xl font-bold text-green-700">{selectedUserActivity.totalLogins}</div>
                    <div className="text-sm text-[#64748b]">Total Logins</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08] text-center">
                    <div className="text-2xl font-bold text-red-700">{selectedUserActivity.failedLoginsLast30Days}</div>
                    <div className="text-sm text-[#64748b]">Failed (30d)</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08] text-center">
                    <div className="text-2xl font-bold text-blue-700">{selectedUserActivity.apiRequestsLast30Days}</div>
                    <div className="text-sm text-[#64748b]">API Requests (30d)</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08] text-center">
                    <div className="text-2xl font-bold text-purple-700">{selectedUserActivity.activeApiKeys}</div>
                    <div className="text-sm text-[#64748b]">Active Keys</div>
                  </div>
                </div>

                {/* Recent Logins */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center text-[#0f172a]">
                    <Activity className="w-5 h-5 mr-2" />
                    Recent Logins
                  </h4>
                  <div className="space-y-2">
                    {selectedUserActivity.recentLogins.map((login) => (
                      <div key={login.id} className="p-3 rounded-lg bg-white/40 backdrop-blur-sm border border-black/[0.08] flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {login.success ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-600" />
                          )}
                          <div>
                            <div className="text-sm text-[#0f172a]">{formatDateTime(login.attemptedAt)}</div>
                            {!login.success && login.failureReason && (
                              <div className="text-xs text-red-700">{login.failureReason}</div>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-[#94a3b8]">{login.ipAddress}</div>
                      </div>
                    ))}
                    {selectedUserActivity.recentLogins.length === 0 && (
                      <p className="text-center text-[#94a3b8] py-4">No login history</p>
                    )}
                  </div>
                </div>

                {/* Recent Account Changes */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center text-[#0f172a]">
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Recent Account Changes
                  </h4>
                  <div className="space-y-2">
                    {selectedUserActivity.recentChanges.map((change) => (
                      <div key={change.id} className="p-3 rounded-lg bg-white/40 backdrop-blur-sm border border-black/[0.08]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm text-[#0f172a]">{change.changeType}</span>
                          <span className="text-xs text-[#94a3b8]">{formatDateTime(change.changedAt)}</span>
                        </div>
                        <p className="text-sm text-[#64748b]">{change.description}</p>
                        <p className="text-xs text-[#94a3b8] mt-1">By: {change.changedByName}</p>
                      </div>
                    ))}
                    {selectedUserActivity.recentChanges.length === 0 && (
                      <p className="text-center text-[#94a3b8] py-4">No account changes</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-[#94a3b8] py-12">Failed to load activity data</p>
            )}
          </div>
        </div>
      )}

      {/* User Permissions Modal */}
      {showPermissionsModal && permissionsUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full p-6 border border-black/[0.08] shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#0f172a]">Section Permissions</h3>
              <button
                onClick={() => { setShowPermissionsModal(false); setPermissionsUser(null); }}
                className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors text-[#64748b]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 rounded-xl bg-white/40 backdrop-blur-sm border border-black/[0.08]">
              <p className="font-medium text-[#0f172a]">{permissionsUser.firstName} {permissionsUser.lastName}</p>
              <p className="text-sm text-[#64748b]">{permissionsUser.email}</p>
            </div>

            <p className="text-sm text-[#64748b] mb-4">
              Control which sections this user can access. Changes take effect on their next login or page refresh.
            </p>

            <div className="space-y-3 mb-6">
              <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                <span className="text-sm font-medium text-[#0f172a]">NMVTIS Record</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={permissionsForm.canAccessNmvtis}
                    onChange={(e) => setPermissionsForm({ ...permissionsForm, canAccessNmvtis: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                </div>
              </label>

              <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                <span className="text-sm font-medium text-[#0f172a]">Theft/Lien Check</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={permissionsForm.canAccessTheftLien}
                    onChange={(e) => setPermissionsForm({ ...permissionsForm, canAccessTheftLien: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                </div>
              </label>

              <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                <span className="text-sm font-medium text-[#0f172a]">Motor Vehicle Record</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={permissionsForm.canAccessMotorVehicleRecord}
                    onChange={(e) => setPermissionsForm({ ...permissionsForm, canAccessMotorVehicleRecord: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                </div>
              </label>

              <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                <span className="text-sm font-medium text-[#0f172a]">VIN Decoder</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={permissionsForm.canAccessVinDecoder}
                    onChange={(e) => setPermissionsForm({ ...permissionsForm, canAccessVinDecoder: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                </div>
              </label>

              <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                <span className="text-sm font-medium text-[#0f172a]">ASTS Public Inquiry</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={permissionsForm.canAccessAstsPublic}
                    onChange={(e) => setPermissionsForm({ ...permissionsForm, canAccessAstsPublic: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                </div>
              </label>

              {user?.isSystemAdmin && (
                <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                  <span className="text-sm font-medium text-[#0f172a]">Invoicing</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={permissionsForm.canAccessInvoicing ?? false}
                      onChange={(e) => setPermissionsForm({ ...permissionsForm, canAccessInvoicing: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                  </div>
                </label>
              )}
              {user?.isSystemAdmin && (
                <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                  <span className="text-sm font-medium text-[#0f172a]">Manage Clients</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={permissionsForm.canManageClients ?? false}
                      onChange={(e) => setPermissionsForm({ ...permissionsForm, canManageClients: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                  </div>
                </label>
              )}
              {user?.isSystemAdmin && (
                <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                  <span className="text-sm font-medium text-[#0f172a]">Require MFA</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={permissionsForm.mfaEnabled ?? true}
                      onChange={(e) => setPermissionsForm({ ...permissionsForm, mfaEnabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                  </div>
                </label>
              )}
              {user?.isSystemAdmin && permissionsUser?.isSystemAdmin && (
                <label className="flex items-center justify-between p-3 rounded-xl border border-black/[0.08] hover:bg-black/[0.02] transition-colors cursor-pointer">
                  <div>
                    <span className="text-sm font-medium text-[#0f172a]">Portal Alert Emails</span>
                    <p className="text-xs text-gray-500 mt-0.5">Receive email when a state portal fails</p>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={permissionsForm.receivePortalAlerts ?? false}
                      onChange={(e) => setPermissionsForm({ ...permissionsForm, receivePortalAlerts: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c41e3a]"></div>
                  </div>
                </label>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSavePermissions}
                disabled={savingPermissions}
                className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-[#c41e3a] to-[#e74860] hover:from-[#a51830] hover:to-[#c41e3a] text-white font-medium transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {savingPermissions ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                <span>{savingPermissions ? 'Saving...' : 'Save Permissions'}</span>
              </button>
              <button
                onClick={() => { setShowPermissionsModal(false); setPermissionsUser(null); }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog?.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full p-6 border border-black/[0.08] shadow-2xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.variant === 'danger' ? 'bg-red-500/15' : 'bg-yellow-500/15'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${
                  confirmDialog.variant === 'danger' ? 'text-red-700' : 'text-yellow-700'
                }`} />
              </div>
              <h3 className="text-xl font-bold text-[#0f172a]">{confirmDialog.title}</h3>
            </div>
            <p className="text-[#64748b] mb-6">{confirmDialog.message}</p>
            <div className="flex space-x-3">
              <button
                onClick={confirmDialog.onConfirm}
                className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all ${
                  confirmDialog.variant === 'danger'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                }`}
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmDialog(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menus */}
      {userActionMenu && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setUserActionMenu(null)}
          />
          {(() => {
            const u = users.find(u => u.id === userActionMenu);
            if (!u) return null;
            return (
              <div
                className="fixed w-48 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border border-black/[0.08] py-2 z-[9999]"
                style={{ top: menuPos.top, left: menuPos.left }}
              >
                <button
                  onClick={() => handleViewActivity(u)}
                  className="w-full px-4 py-2 text-left hover:bg-black/[0.05] flex items-center space-x-2 text-[#0f172a]"
                >
                  <Activity className="w-4 h-4" />
                  <span>View Activity</span>
                </button>
                <button
                  onClick={() => handleToggleUserStatus(u)}
                  className="w-full px-4 py-2 text-left hover:bg-black/[0.05] flex items-center space-x-2 text-[#0f172a]"
                >
                  {u.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                  <span>{u.isActive ? 'Deactivate' : 'Activate'}</span>
                </button>
                <button
                  onClick={() => handleChangeRole(u)}
                  className="w-full px-4 py-2 text-left hover:bg-black/[0.05] flex items-center space-x-2 text-[#0f172a]"
                >
                  <Shield className="w-4 h-4" />
                  <span>Make {u.role === 'ClientAdmin' ? 'User' : 'Admin'}</span>
                </button>
                <button
                  onClick={() => handleOpenPermissions(u)}
                  className="w-full px-4 py-2 text-left hover:bg-black/[0.05] flex items-center space-x-2 text-[#0f172a]"
                >
                  <Lock className="w-4 h-4" />
                  <span>Permissions</span>
                </button>
                <hr className="my-2 border-black/[0.08]" />
                {u.markedForDeletionAt ? (
                  <button
                    onClick={() => handleRestoreUser(u)}
                    className="w-full px-4 py-2 text-left hover:bg-green-500/10 text-green-700 flex items-center space-x-2"
                  >
                    <UserCheck className="w-4 h-4" />
                    <span>Restore User</span>
                  </button>
                ) : (
                  <button
                    onClick={() => handleDeleteUser(u)}
                    className="w-full px-4 py-2 text-left hover:bg-red-500/10 text-red-700 flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete User</span>
                  </button>
                )}
              </div>
            );
          })()}
        </>,
        document.body
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default Management;
